import * as THREE from 'three';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { degToRad } from "three/src/math/MathUtils.js";
import { MathUtils } from "three/src/math/MathUtils.js";
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import Papa from 'papaparse';
import starVertexShader from './shaders/vertex.glsl';
import starFragmentShader from './shaders/fragment.glsl';

class App {
    #threejs = null;
    #shotCam = null;
    #uiCam = null;
    #shotCamGroup = null;
    #camerascene = null;
    // #cameraHelper = null;
    #scene = null;
    #sceneUI = null;
    #controls = null;
    #clock = new THREE.Timer ();
    #fontLoader = new FontLoader ();
    #header = null;
    #headerSub = null;
    #demoReelText = null;
    #resumeText = null;
    #mesh = null;
    // #ground = null;
    #hitAreaMesh = null;
    #resumeBox = null;
    #testBody = null;
    // #exploreText = null;
    #borderWidth = null;
    #borderHeight = null;
    #borderLeft = null;
    #borderRight = null;
    #borderTop = null;
    #borderBtm = null;
    #demoReelHit = null;
    #linkedInHit = null;
    #xHit = null;
    #facebookHit = null;
    #instagramHit = null;
    #linkedInLogo = null;
    #linkedInTex = null;
    #xLogo = null;
    #xTex = null;
    #facebookLogo = null;
    #facebookTex = null;
    #instagramLogo = null;
    #instagramTex = null;
    #demoReelBoxWidth = null;
    #demoReelBoxHeight = null;
    #resumeBoxWidth = null;
    #resumeBoxHeight = null;
    #iconsW = null;
    #iconsH = null;
    #headerRootX = null;
    #headerRootY = null;
    #raycaster = null;
    #bypassLink = null;
    #pointerDownX = 0;
    #pointerDownY = 0;
    #shotCamTargetY = null;
    #shotCam_setViewOffsetX = null;
    #shotCam_setViewOffsetY = null;
    #shotCam_distance = null;
    #newDist = null;
    #controlsChanged = false;
    #loadingManager = null;
    #gltfLoader = null;
    #textureLoader = new THREE.TextureLoader ();
    #north = null;
    #latitude = null;
    #obsTop = null;
    #obsBtm = null;
    #obsScale = null;
    #obsTopInside = null;
    #groundEnv = null;
    #groundEnvTex = null;
    #arrow = null;
    #arrowN = null;
    #arrowNrmlTex = null;
    #arrowAlbTex = null;
    #arrowAlphaTex = null;
    #milkyWayGlowAlbTex = null;
    #mwDark01AlbTex = null;
    #starGroup = null;
    #previousTime = null;
    #skylineTreesTex = null;
    #starArray = null;
    #starPoints = null;
    #hdrTex = null;
    // #arrowMatCapTex = null;
    

    

    constructor () {
        window.addEventListener ('resize', () => {
            this.#onWindowResize ();
            
        });

        this.#initBypassLink ();

        this.#previousTime = performance.now ();

        window.addEventListener ('visibilitychange', () => {
            if (document.hidden) {
                // 1. Reset your custom input states here (e.g., this.isDragging = false)
                
                // 2. Force Three.js OrbitControls (if using them) to reset
                if (this.#controls) {
                    this.#controls.reset (); 
                    // this.#controls.enabled = false;
                    // this.#controls.enabled = true;
                    // Re-enable on a tiny delay so it doesn't snap instantly on return
                    // setTimeout(() => { this.#controls.enabled = true; }, 100);
                }
            }
        });

    }

    initialize () {
        const w = window.innerWidth;
        const h = window.innerHeight;
        this.#demoReelBoxWidth = 120;
        this.#demoReelBoxHeight = 30;
        this.#resumeBoxWidth = 140;
        this.#resumeBoxHeight = 30;
        this.#headerRootX = 20;
        this.#headerRootY = 20;
        this.#shotCam_setViewOffsetX = 50;
        this.#shotCam_setViewOffsetY = -100;
        this.#shotCam_distance = 20;
        this.#north = 45;
        this.#latitude = 32;
        this.#obsScale = 2;
        this.#iconsW = 35;
        this.#iconsH = 35;

        this.#loadingManager = new THREE.LoadingManager ();
        this.#gltfLoader = new GLTFLoader (this.#loadingManager);

        this.#arrowNrmlTex = this.#textureLoader.load ('./models/textures/arrow_nrml_256.png');
        this.#arrowAlbTex = this.#textureLoader.load ('./models/textures/arrow_alb_256.png');
        this.#arrowAlphaTex = this.#textureLoader.load ('./models/textures/arrow_alpha_256.png');
        // this.#arrowMatCapTex = this.#textureLoader.load ('./models/textures/242733_333A4D_3E4554_3C3B43-128px.png');
        // this.#arrowMatCapTex.colorSpace = THREE.SRGBColorSpace;
        // this.#arrowNrmlTex.colorSpace = THREE.LinearSRGBColorSpace;
        this.#arrowAlbTex.colorSpace = THREE.SRGBColorSpace;
        this.#arrowAlphaTex.colorSpace = THREE.SRGBColorSpace;
        this.#arrowNrmlTex.flipY = false;
        this.#arrowAlbTex.flipY = false;
        this.#arrowAlphaTex.flipY = false;
        this.#milkyWayGlowAlbTex = this.#textureLoader.load('./models/textures/mw_layerAll.png');
        this.#milkyWayGlowAlbTex.flipY = true;
        this.#mwDark01AlbTex = this.#textureLoader.load ('./models/textures/mw_layerDark01.png');
        this.#mwDark01AlbTex.flipY = true;
        this.#skylineTreesTex = this.#textureLoader.load ('./models/textures/skylineOfTrees.png');
        this.#skylineTreesTex.colorSpace = THREE.SRGBColorSpace;
        this.#skylineTreesTex.wrapS = THREE.RepeatWrapping;
        this.#skylineTreesTex.repeat.set (3, 1);
        this.#groundEnvTex = this.#textureLoader.load ('./models/textures/ground.png');
        this.#linkedInTex = this.#textureLoader.load ('./models/textures/logo_linkedIn.png');
        this.#xTex = this.#textureLoader.load ('./models/textures/logo_x.png');
        this.#facebookTex = this.#textureLoader.load ('./models/textures/logo_facebook.png');
        this.#instagramTex = this.#textureLoader.load ('./models/textures/logo_instagram.png');

        const canvas = document.querySelector('canvas.webgl');
        // const canvas = document.getElementById('canvas.webgl');
        // document.querySelector('canvas.webgl')

        this.#threejs = new THREE.WebGLRenderer ({ 
            canvas: canvas, 
            alpha: true, 
            antialias: true
        });
        this.#threejs.colorSpace = THREE.LinearSRGBColorSpace;
        this.#threejs.setSize (w, h);
        this.#threejs.shadowMap.enabled = true;
        // this.#threejs.shadowMap.type = THREE.PCFSoftShadowMap;
        // this.#threejs.toneMapping = THREE.ACESFilmicToneMapping;
        this.#threejs.toneMapping = THREE.AgXToneMapping;
        // this.#threejs.toneMapping = THREE.CineonToneMapping;
        // this.#threejs.toneMapping = THREE.LinearToneMapping;
        // this.#threejs.toneMapping = THREE.ReinhardToneMapping;
        // this.#threejs.toneMapping = THREE.NeutralToneMapping;
        this.#threejs.toneMappingExposure = 1.0;
        this.#threejs.shadowMap.type = THREE.PCFShadowMap;
        // document.body.appendChild (this.#threejs.domElement);
        
        const aspect = w / h;
        this.#shotCamTargetY = 100;
        this.#shotCam = new THREE.PerspectiveCamera (55, aspect, 0.1, 2000);
        // this.#shotCam.position.z = 5;
        // this.#shotCam.position.set (0, 0.5, this.#shotCam_distance);
        this.#shotCam.position.set (0, 0.5, 20);
        
        // this.#shotCam.filmOffset = w * 0.005;
        this.#shotCam.setViewOffset (w, h, this.#shotCam_setViewOffsetX, this.#shotCam_setViewOffsetY, w, h);

        this.#uiCam = new THREE.OrthographicCamera (w / -2, w / 2, h / 2, h / -2, 0.1, 2000);

        this.#controls = new OrbitControls (this.#shotCam, canvas);
        // this.#controls = new OrbitControls (this.#shotCam, this.#threejs.domElement);
        this.#controls.enableDamping = true;
        this.#controls.target.set (0, 0, 0);
        this.#controls.enableZoom = false;
        this.#controls.enablePan = false;
        this.#controls.rotateSpeed = 0.3;

        this.#controls.minPolarAngle = degToRad (88.56790381583535);
        this.#controls.maxPolarAngle = degToRad (135);
        
        const polarAngle = this.#controls.getPolarAngle ();
        const angleInDegrees = polarAngle * (180 / Math.PI);
        console.log ('Polar Angle:', angleInDegrees);

        if (this.#controls) {
            this.#controls.addEventListener ('change', () => {
                this.#controlsChanged = true;
            });
        }
        // this.#shotCam.position.set (0, 0.5, this.#shotCam_distance * this.#newDist);
        
        
        
        

        // this.#cameraHelper = new THREE.CameraHelper (this.#uiCam);

        this.#scene = new THREE.Scene ();
        this.#scene.background = new THREE.Color(0x000000);
        this.#sceneUI = new THREE.Scene ();
        // this.#shotCamGroup = new THREE.Group ();

        /*
        const lightUI = new THREE.DirectionalLight (0xffffff, 1.0);
        lightUI.position.set (0, 5, 20);
        this.#sceneUI.add (lightUI);
        */

        // const ambientLight = new THREE.AmbientLight (0xf4f6fb, 0.05);
        // const ambientLight = new THREE.HemisphereLight (0x0c121d, 0xf4f6fb, 0.1);
        // this.#scene.add (ambientLight);
        
        const obsLight = new THREE.PointLight(0xff0000, 0.4);
        obsLight.position.set (0, 0.5, 0);
        obsLight.castShadow = true;
        this.#scene.add (obsLight);
        
        
        const lightSize = 10;
        // const directionalLight = new THREE.DirectionalLight (0xfffefe, 0.3);
        const directionalLight = new THREE.DirectionalLight (0xf4f6fb, 0.6);
        // directionalLight.position.set (2, 8, 2);
        directionalLight.position.set (10, 5, 10);
        directionalLight.castShadow = true;
        // directionalLight.shadow.camera.near = 0.1;
        // directionalLight.shadow.camera.far= 100;
        directionalLight.shadow.camera.left = -lightSize;
        directionalLight.shadow.camera.right = lightSize;
        directionalLight.shadow.camera.top = lightSize;
        directionalLight.shadow.camera.bottom = -lightSize;
        directionalLight.shadow.mapSize.set (256, 256);
        this.#scene.add (directionalLight);
        
        

        // const shadowHelper = new THREE.CameraHelper (directionalLight.shadow.camera);
        // this.#scene.add (shadowHelper);

        /*
        const rgbeLoader = new RGBELoader ();
        // rgbeLoader.load ('./env/dikhololo_night_1k.hdr', (environmentMap) => {
        // rgbeLoader.load ('./env/moonless_golf_1k.hdr', (environmentMap) => {
        rgbeLoader.load ('./env/rogland_clear_night_1k.hdr', (environmentMap) => {
        // rgbeLoader.load ('./env/sahara_night_no_lamps_1k.hdr', (environmentMap) => {
                environmentMap.mapping = THREE.EquirectangularReflectionMapping;
                // environmentMap.colorSpace = THREE.LinearSRGBColorSpace;
                environmentMap.colorSpace = THREE.SRGBColorSpace;
                this.#scene.environment = environmentMap;
                this.#scene.environmentIntensity = 0.1;
            });
        */

        this.#borderWidth = 10;
        this.#borderHeight = 10;

        const uiOriginX = w / -2 + this.#headerRootX;
        const uiOriginY = h / 2 - this.#headerRootY;

        this.#raycaster = new THREE.Raycaster();

        this.#hitAreaMesh = new THREE.Mesh (
            new THREE.PlaneGeometry (1, 1),
            new THREE.MeshBasicMaterial ({
                color: 0x00ffff,
                visible: false
            })
        );
        
        this.#demoReelHit = new THREE.Mesh (
            this.#hitAreaMesh.geometry,
            this.#hitAreaMesh.material
        );
        // this.#demoReelHit.userData.url = "https://vimeo.com/284534746";
        this.#demoReelHit.userData.url = "https://vimeo.com/mattstrangio/games";

        this.#resumeBox = new THREE.Mesh (
            this.#hitAreaMesh.geometry,
            this.#hitAreaMesh.material 
        );
        this.#resumeBox.userData.url = "http://www.mattstrangio.com/MattStrangio_Resume.pdf";

        this.#linkedInHit = new THREE.Mesh (
            this.#hitAreaMesh.geometry,
            this.#hitAreaMesh.material
        );
        this.#linkedInHit.userData.url = "http://www.linkedin.com/in/mattstrangio";

        this.#xHit = new THREE.Mesh (
            this.#hitAreaMesh.geometry,
            this.#hitAreaMesh.material
        );
        this.#xHit.userData.url = "https://x.com/mattstrangio";

        this.#facebookHit = new THREE.Mesh (
            this.#hitAreaMesh.geometry,
            this.#hitAreaMesh.material
        );
        this.#facebookHit.userData.url = "https://facebook.com/mattstrangio";

        this.#instagramHit = new THREE.Mesh (
            this.#hitAreaMesh.geometry,
            this.#hitAreaMesh.material
        );
        this.#instagramHit.userData.url = "https://instagram.com/mattstrangio";

        this.#linkedInLogo = new THREE.Mesh (
            new THREE.PlaneGeometry (1, 1),
            new THREE.MeshBasicMaterial ({color: 0xffffff, map: this.#linkedInTex})
        );

        this.#xLogo = new THREE.Mesh (
            new THREE.PlaneGeometry (1, 1),
            new THREE.MeshBasicMaterial ({color: 0xffffff, map: this.#xTex})
        );

        this.#facebookLogo = new THREE.Mesh (
            new THREE.PlaneGeometry (1, 1),
            new THREE.MeshBasicMaterial ({color: 0xffffff, map: this.#facebookTex})
        );

        this.#instagramLogo = new THREE.Mesh (
            new THREE.PlaneGeometry (1, 1),
            new THREE.MeshBasicMaterial ({color: 0xffffff, map: this.#instagramTex})
        );

        this.#testBody = new THREE.Mesh (
            new THREE.PlaneGeometry (1, 1),
            new THREE.MeshBasicMaterial ({color: 0x000000, transparent: true, opacity: 0.5, depthWrite: false/*, alphaToCoverage: true*/})
        );

        // this.#borderLeft = new THREE.Mesh (
        //     new THREE.PlaneGeometry (1, 1),
        //     new THREE.MeshBasicMaterial ({
        //         color: 0x000000,
        //     })
        // );

        // this.#borderRight = new THREE.Mesh (
        //     new THREE.PlaneGeometry (1, 1),
        //     new THREE.MeshBasicMaterial ({
        //         color: 0x000000,
        //     })
        // );
        
        this.#demoReelHit.position.set ((w * 0.1) + this.#demoReelBoxWidth / 2 - 12, -42, -1);
        this.#demoReelHit.scale.set (this.#demoReelBoxWidth, this.#demoReelBoxHeight, 1);
        this.#resumeBox.position.set ((w * 0.1) + this.#resumeBoxWidth / 2 - 12, -82, -1);
        this.#resumeBox.scale.set (this.#resumeBoxWidth, this.#resumeBoxHeight, 1);
        this.#linkedInLogo.position.set ((w * 0.1) + this.#iconsW / 2 - 10, -122, -1);
        this.#linkedInLogo.scale.set (25, 25, 1);
        this.#linkedInHit.position.set ((w * 0.1) + this.#iconsW / 2 - 10, -122, -1);
        this.#linkedInHit.scale.set (this.#iconsW, this.#iconsH, 1);
        this.#xLogo.position.set ((w * 0.1) + this.#iconsW / 2 + 27, -122, -1);
        this.#xLogo.scale.set (25, 25, 1);
        this.#xHit.position.set ((w * 0.1) + this.#iconsW / 2 + 27, -122, -1);
        this.#xHit.scale.set (this.#iconsW, this.#iconsH, 1);
        this.#facebookLogo.position.set ((w * 0.1) + this.#iconsW / 2 + 64, -122, -1);
        this.#facebookLogo.scale.set (25, 25, 1);
        this.#facebookHit.position.set ((w * 0.1) + this.#iconsW / 2 + 64, -122, -1);
        this.#facebookHit.scale.set (this.#iconsW, this.#iconsH, 1);
        this.#instagramLogo.position.set ((w * 0.1) + this.#iconsW / 2 + 101, -122, -1);
        this.#instagramLogo.scale.set (25, 25, 1);
        this.#instagramHit.position.set ((w * 0.1) + this.#iconsW / 2 + 101, -122, -1);
        this.#instagramHit.scale.set (this.#iconsW, this.#iconsH, 1);
        this.#testBody.position.set ((w * 0.1) + 60, -83, -2);
        this.#testBody.scale.set (160, 130, 1);

        // this.#borderLeft.position.set (w / -2 + (this.#borderWidth / 2), 0, -1);
        // this.#borderRight.position.set (w / 2 - (this.#borderWidth / 2), 0, -1);
        // this.#borderLeft.scale.set (this.#borderWidth, h, 1);
        // this.#borderRight.scale.set (this.#borderWidth, h, 1);

        // Sky
        // SkyGlow
        const skyGlowGeo = new THREE.SphereGeometry (1, 24, 24, 0, Math.PI * 2, 0, Math.PI / 2);
        const skyGlowMat = new THREE.ShaderMaterial ({
            uniforms: {
                skyColorZenith: {
                    value: new THREE.Color (0x0b1218)
                    // value: new THREE.Color (0x020111)
                },
                skyColorMid: {
                    value: new THREE.Color (0x37516a)
                    // value: new THREE.Color (0x2e2e4c)
                },
                skyColorHorizon: {
                    value: new THREE.Color (0x847967)
                    // value: new THREE.Color (0x2f2f36)
                }
             }, 
             vertexShader: `
                varying vec2 vUv;
        
                void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);
                }
            `,
            fragmentShader: `
                uniform vec3 skyColorHorizon;
                uniform vec3 skyColorZenith;
                uniform vec3 skyColorMid;
          
                varying vec2 vUv;
            
                void main() {
                    vec3 color = vec3(0.0);
                    color = mix(skyColorHorizon, skyColorMid, vUv.y - 0.1);
                    color = mix(color, skyColorZenith, vUv.y * 1.0 + 0.85);
                    gl_FragColor = vec4(color, 0.5);
        
                    #include <tonemapping_fragment>
                    #include <colorspace_fragment>
                }
            `,
            side: THREE.BackSide,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
            transparent: true, 
            alphaHash: true
        })
        const skyGlowMesh = new THREE.Mesh(skyGlowGeo, skyGlowMat);
        skyGlowMesh.position.set (0, 0, 0);
        skyGlowMesh.scale.set (96, 96, 96);

        // Milky Way
        const milkyWayGlowGeo = new THREE.SphereGeometry (98.5, 24, 24);
        const milkyWayGlowMat = new THREE.MeshBasicMaterial ({color: 0xadd1e7, side: THREE.BackSide, transparent: true, map: this.#milkyWayGlowAlbTex, alphaMap: this.#milkyWayGlowAlbTex, opacity: 0.85, depthWrite: false});
        // const milkyWayGlowMat = new THREE.MeshBasicMaterial({color: 0xfffbdb, side: THREE.BackSide, transparent: true, map: milkyWayGlowAlbTex, alphaMap: milkyWayGlowAlbTex, opacity: 0.75, depthWrite: false})
        // milkyWayGlowMat.blending = THREE.CustomBlending
        // milkyWayGlowMat.blendDst = THREE.OneMinusConstantAlphaFactor
        const milkyWayGlow = new THREE.Mesh (milkyWayGlowGeo, milkyWayGlowMat);
        // milkyWayGlow.rotation.set (0, degToRad (- this.#north), degToRad (90 - this.#latitude), 'YXZ');
        // milkyWayGlow.rotateY (degToRad(-this.#north));
        // milkyWayGlow.rotateZ (degToRad(90-this.#latitude));
        // this.#scene.add (milkyWayGlow)
        
        const mwDarkGeo = new THREE.SphereGeometry (98, 24, 24);
        const mwDarkMat = new THREE.MeshBasicMaterial({color: 0x0b0804, side: THREE.BackSide,/*wireframe: true, */ transparent: true, map: this.#mwDark01AlbTex, alphaMap: this.#mwDark01AlbTex, opacity: 0.75, depthWrite: false});
        const milkyWayDark = new THREE.Mesh (mwDarkGeo, mwDarkMat);
        // milkyWayDark.rotation.set (0, degToRad (- this.#north), degToRad (90 - this.#latitude), 'YXZ');
        // milkyWayDark.rotateY(degToRad(-this.#north));
        // milkyWayDark.rotateZ(degToRad(90-this.#latitude));
        // this.#scene.add (milkyWayDark);

        // Groups
        this.#starGroup = new THREE.Group ();
        // this.#starGroup.rotateY (degToRad (90-this.#north));
        const starGroupRoot = new THREE.Group ();
        starGroupRoot.add (this.#starGroup);
        starGroupRoot.rotateY (degToRad (-this.#north));
        starGroupRoot.rotateZ (degToRad (90 - this.#latitude));

        this.#starGroup.add (milkyWayGlow, milkyWayDark);

        // this.#starGroup.rotateY (degToRad (-this.#north));
        // this.#starGroup.rotation.y = degToRad (- this.#north);
        // this.#starGroup.rotation.z = degToRad (90 - this.#latitude);
        // this.#starGroup.rotation.set (0, degToRad (- this.#north), degToRad (90 - this.#latitude), 'YXZ');
        // this.#starGroup.rotateZ (degToRad (90-this.#latitude));
        // scene.add(starGroup)

        // this.#scene.add (skyGlowMesh, milkyWayGlow, milkyWayDark);
        this.#scene.add (skyGlowMesh, starGroupRoot);

        this.#uiCam.add (
            this.#demoReelHit, 
            this.#resumeBox, 
            this.#linkedInLogo, 
            this.#linkedInHit, 
            this.#xLogo, 
            this.#xHit, 
            this.#facebookLogo, 
            this.#facebookHit, 
            this.#instagramLogo, 
            this.#instagramHit, 
            // this.#borderLeft, 
            // this.#borderRight, 
            this.#testBody
        );
        

        // this.#sceneUI.add (this.#cameraHelper);

        this.#scene.add (this.#shotCam);
        this.#sceneUI.add (this.#uiCam);

        //Fonts
        this.#fontLoader.load (
            './fonts/Roboto_Condensed_Bold_Italic.json', (font) =>
            {
                this.#header = new THREE.Mesh (new TextGeometry ('Matt Strangio', {font: font, size: 18, depth: 0, curveSegments: 12}, new THREE.MeshBasicMaterial ({color: 0xffffff, /*transparent: true,*/ opacity: 1.0, depthWrite: false})));
                this.#header.position.set (uiOriginX + 10, uiOriginY - 20, -1);
                this.#sceneUI.add (this.#header);

                // this.#exploreText = new THREE.Mesh (new TextGeometry ('Explore!', {font: font, size: 18, depth: 0, curveSegments: 12}, new THREE.MeshBasicMaterial ({color: 0xffffff, /*transparent: true,*/ opacity: 1.0, depthWrite: false})));
                // this.#exploreText.position.set (w * 0.1, -200, -1);
                // this.#sceneUI.add (this.#exploreText);
            }
        )
        this.#fontLoader.load (
            './fonts/Roboto_Condensed_Medium.json', (font) =>
            {
                this.#headerSub = new THREE.Mesh (new TextGeometry ('Animator/3D Dev Artist', {font: font, size: 10, depth: 0, curveSegments: 12}, new THREE.MeshBasicMaterial ({color: 0xffffff, /*transparent: true,*/ opacity: 1.0, depthWrite: false})));
                this.#headerSub.position.set (uiOriginX + 30, uiOriginY - 38, -1);
                this.#sceneUI.add (this.#headerSub);

                this.#demoReelText = new THREE.Mesh (new TextGeometry ('Demo Reel', {font: font, size: 16, depth: 0, curveSegments: 12}, new THREE.MeshBasicMaterial ({color: 0xffffff, /*transparent: true,*/ opacity: 1.0, depthWrite: false})));
                this.#demoReelText.position.set (w * 0.1 - 2, -50, -1);
                this.#sceneUI.add (this.#demoReelText);
                
                this.#resumeText = new THREE.Mesh (new TextGeometry ('Resumé / CV', {font: font, size: 16, depth: 0, curveSegments: 12}, new THREE.MeshBasicMaterial ({color: 0xffffff, /*transparent: true,*/ opacity: 1.0, depthWrite: false})));
                this.#resumeText.position.set (w * 0.1 - 2, -90, -1);
                this.#sceneUI.add (this.#resumeText);
            }
        )

        this.#threejs.autoClear = false;
        
        this.#onWindowResize ();
        this.#setupLoadingManager ();
        this.#loadAssets ();
        // this.#cameraChange ();
        // this.#scrollY ();
        this.#raf ();
        }

        #onWindowResize () {
            if (!this.#obsTop || !this.#obsTop.scene) {
                return; 
            }
            // this.#obsTop.scene.rotation.set (0, degToRad (60), 0);
            this.#obsTop.scene.scale.set (this.#obsScale, this.#obsScale, this.#obsScale);
            this.#obsBtm.scene.scale.set (this.#obsScale, this.#obsScale, this.#obsScale);
            // this.#obsTopInside.scene.rotation.set (0, degToRad (60), 0);
            this.#obsTopInside.scene.scale.set (this.#obsScale, this.#obsScale, this.#obsScale);

            const w = window.innerWidth;
            const h = window.innerHeight;
            const aspect = w / h;
            const uiOriginX = w / -2 + this.#headerRootX;
            const uiOriginY = h / 2 - this.#headerRootY;

            // this.#shotCam.filmOffset = w * 0.005;
            this.#shotCam.setViewOffset (w, h, this.#shotCam_setViewOffsetX, this.#shotCam_setViewOffsetY, w, h);

            this.#threejs.setSize (w, h);
            this.#shotCam.aspect = aspect;
            // this.#shotCam.rotation.x = degToRad(45);
            // this.#shotCam.lookAt (0, this.#shotCamTargetY, 0);
            this.#uiCam.left = w / -2;
            this.#uiCam.right = w / 2;
            this.#uiCam.top = h / 2;
            this.#uiCam.bottom = h / -2;

            // this.#starGroup.rotateY (degToRad (-this.#north));
            // this.#starGroup.rotateZ (degToRad (90 - this.#latitude));

            this.#demoReelHit.position.set ((w * 0.1) + this.#demoReelBoxWidth / 2 - 12, -42, -1);
            this.#resumeBox.position.set ((w * 0.1) + this.#resumeBoxWidth / 2 - 12, -82, -1);
            this.#linkedInLogo.position.set ((w * 0.1) + this.#iconsW / 2 - 10, -122, -1);
            this.#linkedInHit.position.set ((w * 0.1) + this.#iconsW / 2 - 10, -122, -1);
            this.#xLogo.position.set ((w * 0.1) + this.#iconsW / 2 + 27, -122, -1);
            this.#xHit.position.set ((w * 0.1) + this.#iconsW / 2 + 27, -122, -1);
            this.#facebookLogo.position.set ((w * 0.1) + this.#iconsW / 2 + 64, -122, -1);
            this.#facebookHit.position.set ((w * 0.1) + this.#iconsW / 2 + 64, -122, -1);
            this.#instagramLogo.position.set ((w * 0.1) + this.#iconsW / 2 + 101, -122, -1);
            this.#instagramHit.position.set ((w * 0.1) + this.#iconsW / 2 + 101, -122, -1);
            // this.#borderLeft.position.set (w / -2 + (this.#borderWidth / 2), 0, -1);
            // this.#borderRight.position.set (w / 2 - (this.#borderWidth / 2), 0, -1);

            this.#testBody.position.set ((w * 0.1) + 60, -83, -2);

            // this.#borderLeft.scale.set (this.#borderWidth, h, 1);
            // this.#borderRight.scale.set (this.#borderWidth, h, 1);

            this.#arrow.scene.position.set (-(window.innerWidth * 0.0005), -1.49, -5);
            this.#arrowN.position.set (-(window.innerWidth * 0.0005), -1.5, -5);

            if (this.#header) {
                this.#header.position.set (uiOriginX + 10, uiOriginY - 20, -1);
            }
            if (this.#headerSub) {
                this.#headerSub.position.set (uiOriginX + 30, uiOriginY - 38, -1);
            }
            if (this.#demoReelText) {
                this.#demoReelText.position.set (w * 0.1 - 2, -50, -1);
            }
            if (this.#resumeText) {
                this.#resumeText.position.set (w * 0.1 - 2, -90, -1);
            }
            // if (this.#exploreText) {
            //     this.#exploreText.position.set (w * 0.1, -200, -1);
            // }

            // this.#cameraHelper.update ();

            this.#shotCam.updateProjectionMatrix ();
            this.#uiCam.updateProjectionMatrix ();
            

            // console.log (`Resizing to ${w} x ${h}`);
        }

        #initBypassLink () {
            this.#bypassLink = document.getElementById('three-bypass-link');
        
            window.addEventListener('pointerdown', (e) => this.#onPointerDown(e));
            window.addEventListener('pointerup', (e) => this.#onPointerUp(e));
        }

        #onPointerDown (event) {
            const isTouch = event.changedTouches && event.changedTouches.length > 0;
            const clientX = isTouch ? event.changedTouches[0].clientX : event.clientX;
            const clientY = isTouch ? event.changedTouches[0].clientY : event.clientY;
            
            this.#pointerDownX = clientX;
            this.#pointerDownY = clientY;
        }

        #onPointerUp (event) {
            const isTouch = event.changedTouches && event.changedTouches.length > 0;
            const clientX = isTouch ? event.changedTouches[0].clientX : event.clientX;
            const clientY = isTouch ? event.changedTouches[0].clientY : event.clientY;

            const moveDistance = Math.hypot(clientX - this.#pointerDownX, clientY - this.#pointerDownY);
            if (moveDistance > 8) return; 

            const mouse = new THREE.Vector2();
            mouse.x = (clientX / window.innerWidth) * 2 - 1;
            mouse.y = -(clientY / window.innerHeight) * 2 + 1;

            this.#raycaster.setFromCamera(mouse, this.#uiCam);
            const intersects = this.#raycaster.intersectObjects ([
                this.#demoReelHit, 
                this.#resumeBox,
                this.#linkedInHit,
                this.#xHit,
                this.#facebookHit,
                this.#instagramHit
            ], true);

            if (intersects.length > 0) {
                this.#controls.saveState ();

                if (this.#bypassLink) {
                    const hitObject = intersects[0].object;

                    if (hitObject.userData.url) {
                        this.#bypassLink.href = hitObject.userData.url;
                        this.#bypassLink.click();
                    }
                }
            }
        }

        #setupLoadingManager () {
            const progressBar = document.getElementById('progress-bar');
            const loadingScreen = document.getElementById('loading-screen');

            this.#loadingManager.onStart = (url, itemsLoaded, itemsTotal) => {
                console.log (`Started loading: ${url}`);
            };

            this.#loadingManager.onProgress = (url, itemsLoaded, itemsTotal) => {
                const progressRatio = (itemsLoaded / itemsTotal) * 100;
                if (progressBar) progressBar.value = progressRatio;
            };

            this.#loadingManager.onLoad = () => {
                if (loadingScreen) {
                    loadingScreen.classList.add ('fade-out');
                    loadingScreen.addEventListener ('transitionend', (e) => e.target.remove ());
                }
            };

            this.#loadingManager.onError = (url) => {
                console.error (`There was an error loading: ${url}`);
            };
        }

        async #loadAssets () {
            try {
                console.log("Starting model download...");
                
                // Observatory
                this.#obsTop = await this.#gltfLoader.loadAsync ('./models/obsTop.gltf');
                const obsTopNrmlTex = this.#textureLoader.load ('./models/textures/obsTop_nrml_512.png');
                // obsTopNrmlTex.colorSpace = THREE.SRGBColorSpace;
                obsTopNrmlTex.flipY = false;
                const obsTopMat = new THREE.MeshStandardMaterial ({ color: 0xdddddd, metalness: 0.6, roughness: 0.6, normalMap: obsTopNrmlTex, side: THREE.DoubleSide});
                this.#obsTop.scene.children[0].material = obsTopMat;
                // this.#obsTop.scene.children[0].material.environmentMap = this.#hdrTex;
                // this.#obsTop.scene.childred[0].environmentIntensity = 0.3;
                // this.#scene.environmentIntensity = 0.3;
                // this.#obsTop.scene.children[0].material.needsUpdate = true;

                const obs_Top = this.#obsTop.scene.getObjectByName ('Obs_Top');
                const obs_LDoor = this.#obsTop.scene.getObjectByName ('Obs_LDoor');
                const obs_RDoor = this.#obsTop.scene.getObjectByName ('Obs_RDoor');
                // this.#obsTop.castShadow = true;
                // this.#obsTop.receiveShadow = true;
                obs_Top.castShadow = true;
                obs_Top.receiveShadow = true;
                obs_LDoor.castShadow = true;
                obs_LDoor.receiveShadow = true;
                obs_RDoor.castShadow = true;
                obs_RDoor.receiveShadow = true;
                
                obs_Top.rotation.y = degToRad (60);
                
                obs_LDoor.position.z = 0.1;
                obs_RDoor.position.z = 0.1;
                

                this.#obsBtm = await this.#gltfLoader.loadAsync ('./models/obsBtm.gltf');
                const obsBtmMat = new THREE.MeshStandardMaterial ({ color: 0xaaaaaa, metalness: 0.1, roughness: 0.9, /*side: THREE.DoubleSide*/});
                this.#obsBtm.scene.children[0].material = obsBtmMat;
                this.#obsBtm.castShadow = true;
                this.#obsBtm.receiveShadow = true;
                this.#obsBtm.scene.renderOrder = 1;

                this.#obsTopInside = await this.#gltfLoader.loadAsync ('./models/obsTopInside.gltf');
                const obsTopInsideMat = new THREE.MeshStandardMaterial ({ color: 0xaa0000, metalness: 0.1, roughness: 0.9, side: THREE.DoubleSide});
                this.#obsTopInside.scene.children[0].material = obsTopInsideMat;
                this.#obsTopInside.scene.rotation.set (0, degToRad (60), 0);
                // this.#obsTopInside.scene.position.set (0, -0.1, 0.1);
                // this.#obsTopInside.castShadow = true;
                // this.#obsTopInside.receiveShadow = true;
                // this.#obsTopInside.scene.renderOrder = 1;

                this.#obsTop.scene.scale.set (this.#obsScale, this.#obsScale, this.#obsScale);
                this.#obsBtm.scene.scale.set (this.#obsScale, this.#obsScale, this.#obsScale);
                this.#obsTopInside.scene.scale.set (this.#obsScale, this.#obsScale, this.#obsScale);

                // Ground
                this.#groundEnv = await this.#gltfLoader.loadAsync ('./models/ground.gltf');
                const groundEnvMat = new THREE.MeshStandardMaterial ({ color: 0x888888, map: this.#groundEnvTex, metalness: 0.0, roughness: 1.0});
                // const groundEnvMat = new THREE.MeshStandardMaterial ({ color: 0x888888, metalness: 0.2, roughness: 0.9});
                this.#groundEnv.scene.children[0].material = groundEnvMat;
                this.#groundEnv.scene.children[0].receiveShadow = true;
                this.#groundEnv.scene.position.set (0, 0, 0);
                this.#groundEnv.scene.scale.set (2, 2, 2);

                // Arrows
                this.#arrow = await this.#gltfLoader.loadAsync ('./models/arrow.gltf');
                this.#arrow.scene.rotation.set ( degToRad (this.#latitude), degToRad (this.#north), 0, 'YXZ');
                this.#arrow.scene.position.set (-(window.innerWidth * 0.0005), -1.49, -5);
                this.#arrow.scene.scale.set (8, 8, 8);
                // const arrowMat = new THREE.MeshMatcapMaterial({ matcap: arrowMatCapTex })
                const arrowMat = new THREE.MeshStandardMaterial ({ color: 0xaaaaaa, metalness: 0.1, roughness: 0.9});
                
                this.#arrowN = this.#arrow.scene.clone ();
                this.#arrowN.rotation.set ( 0, degToRad (this.#north), 0, 'YXZ');
                this.#arrowN.position.set (-(window.innerWidth * 0.0005), -1.5, -5);
                this.#arrowN.scale.set (7.99, 8, 8);
                // const arrowNMat = new THREE.MeshMatcapMaterial({ matcap: arrowMatCapTex, transparent: false, normalMap: arrowNrmlTex, map: arrowAlbTex, alphaMap: arrowAlphaTex, opacity: 1.0})
                const arrowNMat = new THREE.MeshStandardMaterial ({ color: 0x888888, metalness: 0.1, roughness: 0.9, transparent: false, normalMap: this.#arrowNrmlTex, map: this.#arrowAlbTex, alphaMap: this.#arrowAlphaTex, opacity: 1.0});
                this.#arrow.scene.children[0].material = arrowMat;
                this.#arrowN.children[0].material = arrowNMat;

                // Skyline of Trees
                const skylineTreesGeo = new THREE.CylinderGeometry (90, 90, 10, 24, 1, true);
                const skylineTreesMat = new THREE.MeshBasicMaterial ({ color: 0x000000, map: this.#skylineTreesTex, alphaMap: this.#skylineTreesTex, side: THREE.BackSide, transparent: true, alphaHash: true, depthWrite: false});
                const skylineTreesMesh = new THREE.Mesh (skylineTreesGeo, skylineTreesMat);
                skylineTreesMesh.position.set (0, 4, 0);
                skylineTreesMesh.renderOrder = 2;

                // Fetch CSV Data
                var starData
                this.#starArray = [];
                
                await fetch('./csv/hygData_v1_6mag.csv')
                    .then(response => response.text())
                    .then(text => {
                    // Parse the CSV data
                    starData = Papa.parse(text, {header: true}).data;
                
                    for (var i = 0; i < starData.length; i++) {
                        this.#starArray [i] = starData[i]
                    }
                })
                .then(() => {
                    // console.log(starData[1].starRA); 
                });

                // Stars
                const starParameters = {}
                starParameters.count = this.#starArray.length
                starParameters.distance = 95
                starParameters.maxMag = -1.44
                starParameters.minMag = 5.99
                starParameters.minScale = 0.1
                starParameters.maxScale = 10
                
                const starGeo = new THREE.BufferGeometry()
                const starPositions = new Float32Array(starParameters.count * 3)
                const starColors = new Float32Array(starParameters.count * 3)
                const starScales = new Float32Array(starParameters.count * 1)
                
                for (let i = 0; i < starParameters.count; i++)
                {
                    const star3 = i * 3
                    starPositions[star3   ] = (Math.cos((this.#starArray[i].starRA / 24) * Math.PI * 2)) * (Math.cos(degToRad(this.#starArray[i].starDec)) * starParameters.distance)
                    starPositions[star3 + 1] = (Math.sin(degToRad(this.#starArray[i].starDec)) * starParameters.distance)
                    starPositions[star3 + 2] = -(Math.sin((this.#starArray[i].starRA / 24) * Math.PI * 2)) * (Math.cos(degToRad(this.#starArray[i].starDec)) * starParameters.distance)
                    starColors[star3   ] = this.#starArray[i].colorR
                    starColors[star3 + 1] = this.#starArray[i].colorG
                    starColors[star3 + 2] = this.#starArray[i].colorB
                    starScales[i   ] = starParameters.maxScale - ((this.#starArray[i].mag / -(starParameters.maxMag - starParameters.minMag)) * 1.15 /* Extra Scale Factor */ * (starParameters.maxScale - starParameters.minScale))
                }
                starGeo.setAttribute ('position', new THREE.BufferAttribute(starPositions, 3))
                starGeo.setAttribute ('color', new THREE.BufferAttribute(starColors, 3))
                starGeo.setAttribute ('scale', new THREE.BufferAttribute(starScales, 1))
                    
                const starMat = new THREE.ShaderMaterial({
                    depthWrite: false,
                    blending: THREE.AdditiveBlending, 
                    vertexShader: starVertexShader,
                    fragmentShader: starFragmentShader,
                    uniforms:
                    {
                        starSizeMult: {value: 1.2}
                    }
                })
                starMat.vertexColors = true
                this.#starPoints = new THREE.Points (starGeo, starMat);

                this.#starGroup.add (this.#starPoints);
                this.#scene.add (this.#obsTop.scene, this.#obsBtm.scene, this.#obsTopInside.scene, this.#groundEnv.scene, skylineTreesMesh);
                this.#shotCam.add (this.#arrow.scene, this.#arrowN);
                console.log("Model successfully loaded!", this.#obsTop, this.#obsBtm, this.#obsTopInside);
                
            } catch (error) {
                console.error ("The GLTFLoader failed with this error:", error);
            }
        }

        #raf () {
            requestAnimationFrame ((timestamp) => {
                // const deltatime = this.#clock.getDelta ();
                const deltatime = (timestamp - this.#previousTime) / 1000;
                this.#previousTime = timestamp;

                // if (deltatime > 0.1) {
                //     deltatime = 0.1; 
                // }   

                this.#step (deltatime);
                // this.#starGroup.rotation.set (0, deltatime * (degToRad (-2)), 0); // Speed of Sky Rotation
                // this.#starGroup.rotation.y += deltatime * degToRad (-2);
                // console.log (this.#starGroup.rotation.y);
                

                if (this.#controlsChanged) {
                    const polarAngle = this.#controls.getPolarAngle ();
                    const azAngle = this.#controls.getAzimuthalAngle ();
                    // env.scene.rotation.set (0, azAngle, 0)
                    const angleInDegrees = polarAngle * (180 / Math.PI);
                    const dist = MathUtils.mapLinear (angleInDegrees, 88.56790381583535, 135, 0, 7);
                    const distGround = MathUtils.mapLinear (angleInDegrees, 88.56790381583535, 135, 0, -3);
                    const distObs = MathUtils.mapLinear (angleInDegrees, 88.56790381583535, 135, 0, -3);
                    const distScale = MathUtils.mapLinear (angleInDegrees, 88.56790381583535, 135, 2, 6);
                    // const arrowNudge = MathUtils.mapLinear (angleInDegrees, 88.56790381583535, 135, 0, -0.25);
                    const arrowElev = MathUtils.mapLinear (angleInDegrees, 88.56790381583535, 135, degToRad (this.#latitude), 0);
                    this.#obsScale = distScale;
                    this.#controls.target.set (0, dist, 0);
                    this.#groundEnv.scene.position.y = distGround;
                    this.#obsTop.scene.position.y = distObs;
                    this.#obsTop.scene.scale.set (distScale, distScale, distScale);
                    this.#obsBtm.scene.position.y = distObs;
                    this.#obsBtm.scene.scale.set (distScale, distScale, distScale);
                    this.#obsTopInside.scene.position.y = distObs;
                    this.#obsTopInside.scene.scale.set (distScale, distScale, distScale);
                    this.#groundEnv.scene.scale.set (distScale, distScale, distScale);

                    this.#arrow.scene.rotation.set ( arrowElev, - (azAngle - degToRad (this.#north)), 0, 'YXZ');
                    // this.#arrow.scene.rotation.set ( degToRad (this.#latitude), - (azAngle - degToRad (this.#north)), 0, 'YXZ');  //stable arrow
                    // this.#arrow.scene.position.set (-(window.innerWidth * 0.0005) + arrowNudge, -1.5 + arrowNudge, -5);
                    this.#arrowN.rotation.set ( 0, - (azAngle - degToRad (this.#north)), 0, 'YXZ');
                    // this.#arrowN.position.set (-(window.innerWidth * 0.0005) + arrowNudge, -1.5 + arrowNudge, -5);
                    
                    // console.log ('Polar Angle:', angleInDegrees);
                }

                this.#render ();
                this.#controlsChanged = false; 
                this.#raf ();
            });
        }

        #step (timeElapsed) {
            
            this.#starGroup.rotation.y += timeElapsed * degToRad (-3);
            // console.log (this.#starGroup.rotation.y);
            this.#controls.update (timeElapsed);
        }

        #render () {
            this.#threejs.render (this.#scene, this.#shotCam);
            this.#threejs.clearDepth();
            this.#threejs.render (this.#sceneUI, this.#uiCam);
            // this.#threejs.clearDepth();
            // this.#threejs.render (this.#scene, this.#shotCam);
        }
};

const app = new App ();
app.initialize ();